import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import {
  Home, GraduationCap, Video, Users, BookOpen, User, Settings, LogOut,
  Bell, Search, X, Flame, Zap, Shield, Target,
  CheckCircle, Circle, TrendingUp, Award,
  Calendar, ArrowRight, Menu, FileText, ChevronLeft, ChevronRight,
  Sparkles, Clock, RefreshCw
} from 'lucide-react';
import Scholarships from './Scholarships';
import Mentors from './Mentors';
import Community from './Community';
import Library from './Library';
import Profile from './Profile';

/* ─── Sidebar nav items ─── */
const sidebarLinks = [
  { id: 'home',        label: 'Dashboard',      icon: Home,          color: '#9333ea', badge: null },
  { id: 'scholarships',label: 'Scholarships',   icon: GraduationCap, color: '#2563eb', badge: null },
  { id: 'mentoring',   label: 'Mentoring',      icon: Video,         color: '#3b82f6', badge: null },
  { id: 'peerconnect', label: 'Peer Connect',   icon: Users,         color: '#10b981', badge: null },
  { id: 'library',     label: 'Digital Library', icon: BookOpen,     color: '#ec4899', badge: null },
  { id: 'profile',     label: 'Profile',        icon: User,          color: '#fb923c', badge: null },
];

/* ─── Quick actions ─── */
const quickActions = [
  { label: 'Browse Scholarships', to: 'scholarships', color: '#2563eb', bg: 'rgba(37,99,235,.06)',  border: 'rgba(37,99,235,.15)', icon: GraduationCap },
  { label: 'Book a Mentor',       to: 'mentoring',    color: '#3b82f6', bg: 'rgba(59,130,246,.06)',  border: 'rgba(59,130,246,.15)', icon: Calendar },
  { label: 'Upload Resource',     to: 'library',      color: '#10b981', bg: 'rgba(16,185,129,.06)',  border: 'rgba(16,185,129,.15)', icon: ArrowRight },
  { label: 'Find Study Partner',  to: 'peerconnect',  color: '#fb923c', bg: 'rgba(251,146,60,.06)',  border: 'rgba(251,146,60,.15)', icon: Users },
  { label: 'Take a Quiz',         to: 'library',      color: '#ef4444', bg: 'rgba(239,68,68,.06)',   border: 'rgba(239,68,68,.15)',  icon: Target },
  { label: 'Achievements',        to: 'profile',      color: '#8b5cf6', bg: 'rgba(139,92,246,.06)',  border: 'rgba(139,92,246,.15)', icon: Award },
];

/* ──────────────────────────────────────────────
   WATER POT — Streak Visualization
   ────────────────────────────────────────────── */
function WaterPot({ streak, todayActive }) {
  const maxStreak = 7;
  const fillPct = Math.min((streak || 0) / maxStreak, 1);
  const [animFill, setAnimFill] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setAnimFill(fillPct), 400);
    return () => clearTimeout(timer);
  }, [fillPct]);

  const waterHeight = animFill * 65; // max 65px of pot
  const isEmpty = (streak || 0) === 0;

  return (
    <div style={{ width: 154, height: 170, position: 'relative', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <style>{`
        @keyframes waterWave {
          0%, 100% { d: path("M 0 8 Q 20 2, 40 8 T 80 8 T 120 8 V 80 H 0 Z"); }
          50% { d: path("M 0 5 Q 20 12, 40 5 T 80 5 T 120 5 V 80 H 0 Z"); }
        }
        @keyframes bubbleRise {
          0% { transform: translateY(0) scale(1); opacity: 0.7; }
          50% { opacity: 1; }
          100% { transform: translateY(-40px) scale(0.3); opacity: 0; }
        }
        @keyframes potGlow {
          0%, 100% { filter: drop-shadow(0 4px 12px rgba(59,130,246,0.15)); }
          50% { filter: drop-shadow(0 6px 20px rgba(59,130,246,0.3)); }
        }
      `}</style>

      <svg width="130" height="140" viewBox="0 0 130 140"
        style={{
          filter: isEmpty ? 'drop-shadow(0 4px 12px rgba(0,0,0,0.08))' : undefined,
          animation: !isEmpty ? 'potGlow 3s ease-in-out infinite' : 'none'
        }}>
        <defs>
          <linearGradient id="potBody" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#d4a574"/>
            <stop offset="50%" stopColor="#c4916a"/>
            <stop offset="100%" stopColor="#a0714e"/>
          </linearGradient>
          <linearGradient id="waterGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#60a5fa"/>
            <stop offset="40%" stopColor="#3b82f6"/>
            <stop offset="100%" stopColor="#2563eb"/>
          </linearGradient>
          <clipPath id="potClip">
            <path d="M 25 35 Q 20 35, 18 45 L 12 110 Q 10 125, 25 128 L 105 128 Q 120 125, 118 110 L 112 45 Q 110 35, 105 35 Z"/>
          </clipPath>
        </defs>

        {/* Pot rim */}
        <ellipse cx="65" cy="36" rx="50" ry="8" fill="#c4916a" stroke="#a0714e" strokeWidth="1.5"/>
        <ellipse cx="65" cy="34" rx="48" ry="7" fill="#d4a574"/>
        <ellipse cx="65" cy="34" rx="44" ry="5" fill={isEmpty ? '#e8d5c4' : '#93c5fd'} opacity="0.5"/>

        {/* Pot body */}
        <path d="M 25 35 Q 20 35, 18 45 L 12 110 Q 10 125, 25 128 L 105 128 Q 120 125, 118 110 L 112 45 Q 110 35, 105 35 Z"
          fill="url(#potBody)" stroke="#a0714e" strokeWidth="1"/>

        {/* Pot highlight */}
        <path d="M 30 40 Q 28 40, 26 48 L 22 100 Q 21 108, 28 110"
          fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="3" strokeLinecap="round"/>

        {/* Water inside pot (clipped) */}
        {!isEmpty && (
          <g clipPath="url(#potClip)">
            <rect x="10" y={128 - waterHeight} width="110" height={waterHeight + 5}
              fill="url(#waterGrad)" opacity="0.85"
              style={{ transition: 'y 1.5s cubic-bezier(.22,1,.36,1), height 1.5s cubic-bezier(.22,1,.36,1)' }}/>
            {/* Wave surface */}
            <path d="M 10 8 Q 30 2, 50 8 T 90 8 T 130 8 V 20 H 10 Z"
              fill="#60a5fa" opacity="0.4"
              style={{ transform: `translateY(${128 - waterHeight - 6}px)`, transition: 'transform 1.5s cubic-bezier(.22,1,.36,1)' }}>
            </path>
          </g>
        )}

        {/* Bubbles when active */}
        {todayActive && [
          { cx: 45, delay: '0s', dur: '2.5s', r: 3 },
          { cx: 65, delay: '0.8s', dur: '2s', r: 2 },
          { cx: 80, delay: '1.5s', dur: '2.8s', r: 2.5 },
          { cx: 55, delay: '0.3s', dur: '3s', r: 1.5 },
        ].map((b, i) => (
          <circle key={i} cx={b.cx} cy={128 - waterHeight / 2} r={b.r}
            fill="rgba(147,197,253,0.8)" style={{ animation: `bubbleRise ${b.dur} ease-out ${b.delay} infinite` }}/>
        ))}
      </svg>

      {/* Center label */}
      <div style={{ position: 'absolute', bottom: 22, left: '50%', transform: 'translateX(-50%)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, pointerEvents: 'none' }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: isEmpty ? '#94a3b8' : '#2563eb', lineHeight: 1, fontFamily: 'monospace' }}>
          {streak || 0}
        </div>
        <div style={{ fontSize: 8, color: '#64748b', letterSpacing: '.15em', textTransform: 'uppercase', fontWeight: 700 }}>
          day streak
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────
   WATER BUBBLE STREAK — 7-day strip
   ────────────────────────────────────────────── */
function WaterBubbleStreak({ streak, consistency, todayActive }) {
  const days = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];
  const today = new Date().getDay();
  const todayIdx = today === 0 ? 6 : today - 1;
  const weekDays = consistency?.days || [];

  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
      <style>{`
        @keyframes waterDrop {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.15); }
        }
        @keyframes ripple {
          0% { box-shadow: 0 0 0 0 rgba(59,130,246,0.4); }
          100% { box-shadow: 0 0 0 8px rgba(59,130,246,0); }
        }
      `}</style>
      {days.map((d, i) => {
        const isToday = i === todayIdx;
        const isPastStreakDay = streak > 0 && i <= todayIdx && (todayIdx - i) < streak;
        const isActive = !!weekDays[i]?.completed || (isToday && (todayActive || streak > 0)) || isPastStreakDay;

        return (
          <div key={d} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <div style={{
              width: isToday ? 28 : 22, height: isToday ? 28 : 22,
              borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: isActive ? (isToday ? 13 : 11) : 10,
              fontWeight: isActive ? 800 : 500,
              // Only streak days with performed activity are opaquely bubbled, remaining days normal
              background: isActive
                ? 'linear-gradient(145deg, #3b82f6, #2563eb)'
                : '#ffffff',
              border: isActive
                ? (isToday ? '2px solid #1d4ed8' : '1.5px solid #2563eb')
                : (isToday ? '1.5px solid #94a3b8' : '1px solid #e2e8f0'),
              color: isActive ? '#ffffff' : '#94a3b8',
              boxShadow: isActive ? (isToday ? '0 3px 12px rgba(37,99,235,0.4)' : '0 2px 8px rgba(37,99,235,0.25)') : 'none',
              animation: (isToday && isActive) ? 'ripple 2s ease-out infinite, waterDrop 3s ease-in-out infinite' : 'none',
              transition: 'all 0.3s',
            }}>
              {isActive ? '💧' : '·'}
            </div>
            <div style={{ fontSize: 7, color: (isToday && isActive) ? '#2563eb' : '#94a3b8', fontFamily: 'monospace', fontWeight: isToday ? 800 : 600 }}>{d}</div>
          </div>
        );
      })}
    </div>
  );
}

/* ──────────────────────────────────────────────
   STAT CARD (Scholarships / Mentoring / etc.)
   ────────────────────────────────────────────── */
function StatCard({ barColor, iconBg, icon: Icon, title, rows, linkLabel, onLink, ringColor, ringPct }) {
  const [hovered, setHovered] = useState(false);
  const dashOffset = 100.5 * (1 - (ringPct || 0) / 100);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background:'#ffffff',
        border:`1px solid ${hovered ? ringColor+'44' : 'rgba(0,0,0,.05)'}`,
        borderRadius:20, padding:20, position:'relative', overflow:'hidden',
        transform: hovered ? 'translateY(-4px)' : 'translateY(0)',
        boxShadow: hovered ? `0 12px 30px rgba(0,0,0,.06), 0 0 0 1px ${ringColor}15` : '0 4px 14px rgba(0,0,0,.02)',
        transition:'all .3s cubic-bezier(.22,1,.36,1)', cursor:'default',
      }}
    >
      {/* Top accent bar */}
      <div style={{ position:'absolute', top:0, left:0, right:0, height:3, background:barColor, borderRadius:'20px 20px 0 0' }}/>
      <div style={{ position:'absolute', inset:0, background:'linear-gradient(160deg,rgba(0,0,0,.01) 0%,transparent 60%)', borderRadius:'inherit', pointerEvents:'none' }}/>

      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:14 }}>
        {/* Icon wrapper */}
        <div style={{
          width:44, height:44, borderRadius:14, display:'flex', alignItems:'center', justifyContent:'center',
          background: iconBg, position:'relative', overflow:'hidden', flexShrink:0,
          boxShadow:`0 4px 10px rgba(0,0,0,.08),inset 0 1px 0 rgba(255,255,255,.2)`,
          transform: hovered ? 'translateY(-3px) scale(1.08) rotate(-4deg)' : 'translateY(0) scale(1) rotate(0)',
          transition:'transform .3s cubic-bezier(.22,1,.36,1)',
        }}>
          <div style={{ position:'absolute', inset:0, background:'linear-gradient(150deg,rgba(255,255,255,.22) 0%,transparent 55%)', borderRadius:'inherit' }}/>
          <Icon size={20} color="#ffffff"/>
        </div>
        {/* Ring */}
        <div style={{ position:'relative', width:40, height:40 }}>
          <svg width="40" height="40" viewBox="0 0 40 40" style={{ transform:'rotate(-90deg)' }}>
            <circle cx="20" cy="20" r="16" fill="none" stroke={ringColor+'10'} strokeWidth="3"/>
            <circle cx="20" cy="20" r="16" fill="none" stroke={ringColor} strokeWidth="3"
              strokeDasharray="100.5" strokeDashoffset={dashOffset} strokeLinecap="round"
              style={{ transition:'stroke-dashoffset 1.5s cubic-bezier(.22,1,.36,1)' }}/>
          </svg>
          <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'Poppins,sans-serif', fontSize:9, fontWeight:700, color:ringColor }}>{ringPct}%</div>
        </div>
      </div>

      <div style={{ fontSize:10, fontWeight:700, letterSpacing:'.18em', textTransform:'uppercase', color:'#64748b', marginBottom:12 }}>{title}</div>

      {rows.map(({dot, label, value, valueColor}) => (
        <div key={label} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:7 }}>
          <span style={{ display:'flex', alignItems:'center', gap:7, fontSize:12, fontWeight:500, color:'var(--text-slate)' }}>
            <span style={{ width:5, height:5, borderRadius:'50%', background:dot, flexShrink:0 }}/>
            {label}
          </span>
          <span style={{ fontSize:13, fontWeight:700, color:valueColor }}>{value}</span>
        </div>
      ))}

      <button
        onClick={onLink}
        style={{
          display:'flex', alignItems:'center', gap: hovered ? 9 : 5, fontSize:11, fontWeight:700,
          marginTop:14, paddingTop:12, borderTop:'1px solid rgba(0,0,0,.05)',
          color: ringColor, background:'none', border:'none', cursor:'pointer', fontFamily:'inherit',
          letterSpacing:'.02em', transition:'gap .2s',
        }}
      >
        {linkLabel} <ArrowRight size={12}/>
      </button>
    </div>
  );
}

/* ──────────────────────────────────────────────
   CALENDAR STREAK CARD with Month Navigation & High-Contrast Opacity
   ────────────────────────────────────────────── */
function CalendarStreakCard({ streak, consistency }) {
  const [viewDate, setViewDate] = useState(() => new Date());
  const now = new Date();
  
  const viewYear = viewDate.getFullYear();
  const viewMonth = viewDate.getMonth();
  const isCurrentMonth = viewYear === now.getFullYear() && viewMonth === now.getMonth();

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstDayOfWeek = new Date(viewYear, viewMonth, 1).getDay();
  const startOffset = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1; // Monday start
  const todayDate = now.getDate();
  const monthName = viewDate.toLocaleString('en', { month: 'long' });

  // Set of strictly recorded active dates (YYYY-MM-DD)
  const activeDateStrings = new Set(consistency?.activityDates || []);

  const weekHeaders = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const prevMonth = () => {
    setViewDate(new Date(viewYear, viewMonth - 1, 1));
  };
  const nextMonth = () => {
    setViewDate(new Date(viewYear, viewMonth + 1, 1));
  };
  const resetToToday = () => {
    setViewDate(new Date());
  };

  // Count active days in this viewed month
  let monthlyActiveCount = 0;
  for (let d = 1; d <= daysInMonth; d++) {
    const dStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    if (activeDateStrings.has(dStr)) monthlyActiveCount++;
  }

  return (
    <div style={{ background:'#ffffff', border:'1px solid rgba(0,0,0,0.05)', borderRadius:20, padding:20, position:'relative', overflow:'hidden' }}>
      <style>{`
        @keyframes calBubble {
          0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(59,130,246,0.4); }
          50% { transform: scale(1.06); box-shadow: 0 0 0 6px rgba(59,130,246,0); }
        }
      `}</style>
      <div style={{ position:'absolute', top:0, left:0, right:0, height:3, background:'linear-gradient(90deg,#3b82f6,#60a5fa)', borderRadius:'20px 20px 0 0' }}/>
      
      {/* Header with Month Navigation */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14, position:'relative' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ width:38, height:38, borderRadius:12, background:'linear-gradient(145deg,#3b82f6,#2563eb)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, boxShadow:'0 4px 10px rgba(37,99,235,.2)', position:'relative', overflow:'hidden' }}>
            <div style={{ position:'absolute', inset:0, background:'linear-gradient(150deg,rgba(255,255,255,.22) 0%,transparent 55%)' }}/>
            <span style={{ fontSize:18 }}>💧</span>
          </div>
          <div>
            <div style={{ fontSize:14, fontWeight:700, color:'var(--navy-dark)' }}>Streak Calendar</div>
            <div style={{ fontSize:11, color:'#64748b', fontWeight:600 }}>{monthName} {viewYear}</div>
          </div>
        </div>

        {/* Month Navigation Controls */}
        <div style={{ display:'flex', alignItems:'center', gap:4 }}>
          {!isCurrentMonth && (
            <button
              onClick={resetToToday}
              title="Jump to current month"
              style={{ padding:'4px 8px', background:'rgba(59,130,246,0.08)', border:'1px solid rgba(59,130,246,0.2)', borderRadius:8, fontSize:10, fontWeight:700, color:'#2563eb', cursor:'pointer', marginRight:4 }}
            >
              Today
            </button>
          )}
          <button
            onClick={prevMonth}
            className="btn-lift"
            title="Previous Month"
            style={{ width:28, height:28, borderRadius:8, background:'#f8fafc', border:'1px solid #e2e8f0', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color:'#475569' }}
          >
            <ChevronLeft size={14}/>
          </button>
          <button
            onClick={nextMonth}
            className="btn-lift"
            title="Next Month"
            style={{ width:28, height:28, borderRadius:8, background:'#f8fafc', border:'1px solid #e2e8f0', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color:'#475569' }}
          >
            <ChevronRight size={14}/>
          </button>
        </div>
      </div>

      {/* Grid of Days */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:4, marginBottom:12 }}>
        {weekHeaders.map((h, i) => (
          <div key={i} style={{ fontSize:10, textAlign:'center', color:'#94a3b8', padding:'4px 0', fontWeight:700 }}>{h}</div>
        ))}
        {cells.map((day, i) => {
          if (day === null) return <div key={'e' + i}/>;

          const dateStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const isToday = isCurrentMonth && day === todayDate;
          const isActive = activeDateStrings.has(dateStr);
          const isFuture = viewYear > now.getFullYear() || (viewYear === now.getFullYear() && (viewMonth > now.getMonth() || (isCurrentMonth && day > todayDate)));

          const formattedTooltip = `${monthName.slice(0,3)} ${day}, ${viewYear} • ${
            isToday
              ? (isActive ? '💧 Today (Streak Active!)' : 'Today')
              : isActive
                ? '💧 Streak Maintained'
                : isFuture
                  ? 'Upcoming Day'
                  : 'No Activity'
          }`;

          return (
            <div
              key={day}
              className="streak-bubble-cell"
              title={formattedTooltip}
              style={{
                aspectRatio: '1',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                cursor: 'pointer',
                // ONLY streak days are opaquely bubbled with water, remaining days normal clean calendar cells
                background: isActive
                  ? 'linear-gradient(145deg, #3b82f6, #1d4ed8)'
                  : isToday
                    ? '#eff6ff'
                    : '#ffffff',
                border: isActive
                  ? '1.5px solid #1d4ed8'
                  : isToday
                    ? '1.5px solid #3b82f6'
                    : '1px solid #e2e8f0',
                color: isActive
                  ? '#ffffff'
                  : isToday
                    ? '#2563eb'
                    : isFuture
                      ? '#94a3b8'
                      : '#334155',
                fontSize: isActive ? 12 : 11,
                fontWeight: isActive || isToday ? 800 : 500,
                boxShadow: isActive
                  ? '0 3px 10px rgba(37,99,235,0.35)'
                  : 'none',
                animation: (isToday && isActive) ? 'calBubble 2.5s ease-in-out infinite' : 'none',
                transition: 'all 0.2s cubic-bezier(.22,1,.36,1)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.15)';
                e.currentTarget.style.zIndex = '5';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.zIndex = '1';
              }}
            >
              {isActive ? '💧' : day}
            </div>
          );
        })}
      </div>

      {/* Streak summary badge */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', background:'rgba(59,130,246,0.05)', border:'1px solid rgba(59,130,246,0.15)', borderRadius:12, padding:'10px 14px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ width:34, height:34, borderRadius:10, background:'linear-gradient(145deg,#60a5fa,#3b82f6)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, boxShadow:'0 3px 8px rgba(37,99,235,.15)', position:'relative', overflow:'hidden' }}>
            <div style={{ position:'absolute', inset:0, background:'linear-gradient(150deg,rgba(255,255,255,.2) 0%,transparent 55%)' }}/>
            <span style={{ fontSize:15 }}>🏆</span>
          </div>
          <div>
            <div style={{ fontSize:12, fontWeight:700, color:'var(--navy-dark)' }}>
              {monthlyActiveCount} {monthlyActiveCount === 1 ? 'day' : 'days'} active in {monthName}
            </div>
            <div style={{ fontSize:10, color:'#64748b' }}>
              {streak > 0 ? `${streak}-day active streak` : 'Start your streak today!'}
            </div>
          </div>
        </div>

        <div style={{ textAlign:'right' }}>
          <div style={{ fontSize:18, fontWeight:800, color:'#2563eb', lineHeight:1 }}>{streak || 0}</div>
          <div style={{ fontSize:9, color:'#64748b', fontWeight:700 }}>STREAK</div>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────
   LEADERBOARD CARD
   ────────────────────────────────────────────── */
function LeaderboardCard({ name, score, barsIn, barRef, leaderboard, myRank }) {
  const barHeights = [72, 52, 40];
  const barColors  = ['linear-gradient(180deg,#8b5cf6,#7c3aed)','linear-gradient(180deg,#94a3b8,#475569)','linear-gradient(180deg,#d97706,#b45309)'];
  const podium = [leaderboard[1], leaderboard[0], leaderboard[2]].filter(Boolean);
  const podiumEmoji = ['🥈', '🥇', '🥉'];
  const podiumColors = ['#9333ea', '#64748b', '#d97706'];
  return (
    <div ref={barRef} style={{ background:'#ffffff', border:'1px solid rgba(0,0,0,0.05)', borderRadius:20, padding:20, position:'relative', overflow:'hidden', flex:1 }}>
      <div style={{ position:'absolute', top:0, left:0, right:0, height:3, background:'linear-gradient(90deg,#8b5cf6,#a78bfa)', borderRadius:'20px 20px 0 0' }}/>
      <div style={{ position:'absolute', inset:0, background:'linear-gradient(160deg,rgba(0,0,0,.01) 0%,transparent 60%)', pointerEvents:'none', borderRadius:'inherit' }}/>
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16, position:'relative' }}>
        <div style={{ width:40, height:40, borderRadius:12, background:'linear-gradient(145deg,#8b5cf6,#7c3aed)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, boxShadow:'0 4px 10px rgba(0,0,0,.08),inset 0 1px 0 rgba(255,255,255,.2)', position:'relative', overflow:'hidden' }}>
          <div style={{ position:'absolute', inset:0, background:'linear-gradient(150deg,rgba(255,255,255,.22) 0%,transparent 55%)' }}/>
          <Award size={20} color="rgba(255,255,255,.95)"/>
        </div>
        <div>
          <div style={{ fontSize:14, fontWeight:700, color:'var(--navy-dark)' }}>Leaderboard</div>
          <div style={{ fontSize:10, color:'#64748b' }}>Top scholars this week</div>
        </div>
      </div>
      {/* Podium */}
      <div style={{ display:'flex', alignItems:'flex-end', justifyContent:'center', gap:10, marginBottom:14 }}>
        {podium.map((p, col) => {
          const isFirst = col === 1;
          return (
            <div key={p.rank || col} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:5, flex:1 }}>
              {isFirst && <div style={{ fontSize:14, lineHeight:1, marginBottom:2 }}>👑</div>}
              <div style={{ width:34, height:34, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:17, background: isFirst?'rgba(139,92,246,.1)':'rgba(148,163,184,.08)', border:`1.5px solid ${isFirst?'rgba(139,92,246,.35)':'rgba(148,163,184,.2)'}`, boxShadow: isFirst?'0 3px 8px rgba(139,92,246,.15)':'0 3px 8px rgba(0,0,0,.03)' }}>{podiumEmoji[col]}</div>
              <div style={{ fontSize:10, fontWeight:700, color:'var(--navy-dark)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', maxWidth:70, textAlign:'center' }}>{p.name}</div>
              <div style={{ fontSize:9, fontWeight:600, color:podiumColors[col] }}>{p.score?.toLocaleString() || 0}</div>
              <div style={{ width:'100%', height:barHeights[col===1?0:col===0?1:2], borderRadius:'8px 8px 0 0', background:barColors[col===1?0:col===0?1:2], boxShadow: isFirst?'0 -4px 10px rgba(139,92,246,.15)':'none', transform: barsIn?'scaleY(1)':'scaleY(0)', transformOrigin:'bottom', transition:`transform 1.2s cubic-bezier(.22,1,.36,1) ${.6+col*.12}s` }}/>
              <div style={{ fontSize:9, fontWeight:700, color: isFirst?'#8b5cf6':'#64748b' }}>#{p.rank}</div>
            </div>
          );
        })}
      </div>
      <div style={{ height:1, background:'rgba(0,0,0,.05)', marginBottom:12 }}/>
      <div style={{ display:'flex', alignItems:'center', gap:10, background:'rgba(139,92,246,.04)', border:'1px solid rgba(139,92,246,.12)', borderRadius:14, padding:'11px 14px' }}>
        <div style={{ fontSize:16, fontWeight:900, color:'#8b5cf6', flexShrink:0 }}>#{myRank || '—'}</div>
        <div style={{ width:30, height:30, borderRadius:'50%', background:'linear-gradient(145deg,#8b5cf6,#7c3aed)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, color:'#fff', border:'2px solid rgba(139,92,246,.25)', flexShrink:0, boxShadow:'0 3px 8px rgba(139,92,246,.15)' }}>🎓</div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:12, fontWeight:700, color:'var(--navy-dark)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{name}</div>
          <div style={{ fontSize:10, color:'#64748b' }}>Keep climbing the ranks!</div>
        </div>
        <div style={{ fontSize:13, fontWeight:800, color:'#8b5cf6', flexShrink:0 }}>{score}</div>
      </div>
      <div style={{ marginTop:8, textAlign:'center', background:'rgba(0,0,0,.02)', border:'1px solid rgba(0,0,0,.05)', borderRadius:10, padding:8, fontSize:10, color:'#64748b' }}>
        🚀 Keep studying consistently to crack the Top 1,000!
      </div>
    </div>
  );
}

/* ─── Course Row ─── */
function CourseRow({ name, emoji, pct, color, tag, idx, onClick }) {
  const [w, setW] = useState(0);
  useEffect(() => { setTimeout(() => setW(pct), 300 + idx*80); }, [pct, idx]);
  const [hov, setHov] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        background: hov ? `${color}0c` : '#ffffff',
        border:`1.5px solid ${hov ? color+'35' : 'rgba(0,0,0,.06)'}`,
        borderRadius:14, padding:'11px 14px', cursor:'pointer',
        transform: hov ? 'translateX(3px)' : 'translateX(0)',
        transition:'all .22s cubic-bezier(.22,1,.36,1)',
        position:'relative',
      }}
    >
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:7 }}>
        <div style={{ width:34, height:34, borderRadius:10, display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, background:`${color}12`, border:`1px solid ${color}22`, flexShrink:0,
          boxShadow:`0 2px 8px ${color}15`, transform: hov ? 'scale(1.12) rotate(-4deg)' : 'scale(1)', transition:'transform .3s' }}>
          {emoji}
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:12, fontWeight:700, color:'var(--navy-dark)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{name}</div>
          <div style={{ fontSize:10, color:'#64748b', marginTop:1 }}>{tag}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {hov && <span style={{ fontSize: 10, fontWeight: 700, color: color }}>Study →</span>}
          <div style={{ fontSize:13, fontWeight:800, color:color, flexShrink:0 }}>{pct}%</div>
        </div>
      </div>
      <div style={{ height:5, background:'#f1f5f9', borderRadius:4, overflow:'hidden' }}>
        <div style={{ height:'100%', borderRadius:4, width:`${w}%`, background:`linear-gradient(90deg,${color}70,${color})`, transition:'width 1.8s cubic-bezier(.22,1,.36,1)', boxShadow:`0 0 8px ${color}30` }}/>
      </div>
    </div>
  );
}

/* ─── Upcoming Events ─── */
function UpcomingEventsCard({ events }) {
  return (
    <div style={{ background:'#ffffff', border:'1px solid rgba(0,0,0,0.05)', borderRadius:20, padding:20, position:'relative', overflow:'hidden' }}>
      <div style={{ position:'absolute', top:0, left:0, right:0, height:3, background:'linear-gradient(90deg,#ef4444,#f87171)', borderRadius:'20px 20px 0 0' }}/>
      <div style={{ position:'absolute', inset:0, background:'linear-gradient(160deg,rgba(0,0,0,.01) 0%,transparent 60%)', pointerEvents:'none', borderRadius:'inherit' }}/>
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:14, position:'relative' }}>
        <div style={{ width:3, height:15, background:'linear-gradient(180deg,#8b5cf6,#7e22ce)', borderRadius:2, boxShadow:'0 2px 6px rgba(139,92,246,.2)' }}/>
        <span style={{ fontSize:14, fontWeight:700, color:'var(--navy-dark)' }}>Upcoming Events</span>
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:2, position:'relative' }}>
        {events.length ? events.map((e,i) => <EventRow key={`${e.type}-${e.date}-${i}`} {...e}/>) : <div style={{ color:'#64748b', fontSize:11, padding:'12px 8px' }}>No upcoming events yet.</div>}
      </div>
    </div>
  );
}

function EventRow({ date, title, sub, tag, type }) {
  const [hov, setHov] = useState(false);
  const eventDate = new Date(date);
  const day = String(eventDate.getDate()).padStart(2, '0');
  const month = eventDate.toLocaleString('en', { month: 'short' });
  const mentor = type === 'mentor';
  const tagColor = mentor ? '#8b5cf6' : '#ef4444';
  const tagBg = mentor ? 'rgba(139,92,246,.05)' : 'rgba(239,68,68,.05)';
  const tagBorder = mentor ? 'rgba(139,92,246,.15)' : 'rgba(239,68,68,.15)';
  const calColor = mentor ? 'linear-gradient(135deg,#8b5cf6,#a78bfa)' : 'linear-gradient(135deg,#ef4444,#f87171)';
  return (
    <div
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        display:'flex', alignItems:'center', gap:11, padding:'10px 8px',
        borderRadius:12, cursor:'pointer',
        background: hov ? 'rgba(0,0,0,.02)' : 'transparent',
        borderBottom:'1px solid rgba(0,0,0,.04)',
        transform: hov ? 'translateX(4px)' : 'translateX(0)',
        transition:'all .2s cubic-bezier(.22,1,.36,1)',
      }}
    >
      <div style={{ width:40, height:40, borderRadius:9, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', flexShrink:0, background:calColor, transform: hov?'scale(1.08)':'scale(1)', transition:'transform .2s' }}>
        <div style={{ fontSize:17, lineHeight:1, color:'white', fontWeight:700 }}>{day}</div>
        <div style={{ fontSize:7, color:'rgba(255,255,255,.8)', fontFamily:'monospace', textTransform:'uppercase' }}>{month}</div>
      </div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:11, fontWeight:700, color:'var(--navy-dark)', marginBottom:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{title}</div>
        <div style={{ fontSize:9, color:'#64748b' }}>{sub}</div>
      </div>
      <div style={{ fontSize:8, fontWeight:700, padding:'2px 7px', borderRadius:20, border:`1px solid ${tagBorder}`, background:tagBg, color:tagColor, whiteSpace:'nowrap', fontFamily:'monospace', flexShrink:0 }}>{tag}</div>
    </div>
  );
}

/* ─── Quick Actions Card ─── */
function QuickActionsCard({ onNavigate, goals, goalsDone, goalsPct }) {
  return (
    <div style={{ background:'#ffffff', border:'1px solid rgba(0,0,0,0.05)', borderRadius:20, padding:20, position:'relative', overflow:'hidden', display:'flex', flexDirection:'column' }}>
      <div style={{ position:'absolute', top:0, left:0, right:0, height:3, background:'linear-gradient(90deg,#8b5cf6,#a78bfa)', borderRadius:'20px 20px 0 0' }}/>
      <div style={{ position:'absolute', inset:0, background:'linear-gradient(160deg,rgba(0,0,0,.01) 0%,transparent 60%)', pointerEvents:'none', borderRadius:'inherit' }}/>
      
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8, position:'relative' }}>
        <div style={{ width:3, height:15, background:'linear-gradient(180deg,#8b5cf6,#7e22ce)', borderRadius:2, boxShadow:'0 2px 6px rgba(139,92,246,.2)' }}/>
        <span style={{ fontSize:14, fontWeight:700, color:'var(--navy-dark)' }}>Quick Actions</span>
      </div>

      {/* Daily goals checklist bar */}
      <div style={{ background:'rgba(139,92,246,.04)', border:'1px solid rgba(139,92,246,.12)', borderRadius:12, padding:'10px 14px', marginBottom:14, position:'relative' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
          <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
            {goals.map(g => (
              <span key={g.label} style={{ display:'flex', alignItems:'center', gap:5, fontSize:'11px', fontWeight:700, color: g.done ? '#10b981' : '#64748b' }}>
                {g.done ? <CheckCircle size={13} color="#10b981"/> : <Circle size={13} color="#cbd5e1"/>}
                {g.label}
              </span>
            ))}
          </div>
          <span style={{ fontSize:11, fontWeight:900, color:'#8b5cf6' }}>{goalsPct}%</span>
        </div>
        <div style={{ height:5, background:'#e2e8f0', borderRadius:9999, overflow:'hidden' }}>
          <div style={{ height:'100%', borderRadius:9999, background:'linear-gradient(90deg,#8b5cf6,#7c3aed)', width:`${goalsPct}%`, transition:'width .7s ease', boxShadow:'0 0 8px rgba(139,92,246,.2)' }}/>
        </div>
      </div>

      {/* 6 Quick Action Buttons */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8, position:'relative', flex:1 }}>
        {quickActions.map((a, i) => (
          <QuickActionCard key={i} {...a} onNavigate={onNavigate}/>
        ))}
      </div>
    </div>
  );
}

function QuickActionCard({ label, to, color, bg, border, icon: Icon, onNavigate }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      onClick={() => onNavigate(to)}
      style={{
        display:'flex', flexDirection:'column', alignItems:'center', gap:7,
        padding:'12px 8px', borderRadius:14, cursor:'pointer', textAlign:'center',
        background: hov ? bg : '#f8fafc',
        border:`1px solid ${hov ? border : 'rgba(0,0,0,.05)'}`,
        transform: hov ? 'translateY(-3px) scale(1.04)' : 'translateY(0) scale(1)',
        boxShadow: hov ? `0 8px 20px rgba(0,0,0,.04)` : '0 2px 8px rgba(0,0,0,.01)',
        transition:'all .22s cubic-bezier(.22,1,.36,1)',
        fontFamily:'inherit',
      }}
    >
      <div style={{ width:40, height:40, borderRadius:12, display:'flex', alignItems:'center', justifyContent:'center', background: hov ? bg : '#ffffff', border:`1px solid ${hov ? border : 'rgba(0,0,0,.05)'}`, transition:'all .22s', transform: hov?'rotate(-6deg) scale(1.1)':'rotate(0) scale(1)', color }}>
        <Icon size={18}/>
      </div>
      <div style={{ fontSize:10, fontWeight:600, color:'#475569', lineHeight:1.3 }}>{label}</div>
    </button>
  );
}

/* ──────────────────────────────────────────────
   MAIN DASHBOARD OVERVIEW TAB
   ────────────────────────────────────────────── */
function DashHomeTab({ user, profile, onNavigate }) {
  const [dashboard, setDashboard] = useState(null);
  const [barsIn, setBarsIn]       = useState(false);
  const barRef                    = useRef(null);

  useEffect(() => {
    if (!user) return;
    api.getMyDashboard().then(setDashboard).catch(() => setDashboard(null));
  }, [user]);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if(e.isIntersecting) setBarsIn(true); }, { threshold:0.3 });
    if (barRef.current) obs.observe(barRef.current);
    return () => obs.disconnect();
  }, []);

  const name    = profile?.full_name || user?.user_metadata?.full_name || 'Scholar';
  const score   = dashboard?.score ?? profile?.score ?? 0;
  const streak  = dashboard?.streak ?? profile?.streak ?? 0;
  const stats   = dashboard?.stats || {};

  const defaultCourses = [
    { id: 'reading', name: 'Reading & Research', emoji: '📚', pct: 0, color: '#9333ea', tag: 'Foundation', action: () => onNavigate('library') },
    { id: 'listening', name: 'Video & Lecture Learning', emoji: '🎧', pct: 0, color: '#10b981', tag: 'Learning', action: () => onNavigate('mentoring') },
    { id: 'exploring', name: 'Concept Exploration & Notes', emoji: '🔎', pct: 0, color: '#3b82f6', tag: 'Discovery', action: () => onNavigate('library') },
    { id: 'practicing', name: 'Active Practice & Problem Solving', emoji: '🧩', pct: 0, color: '#8b5cf6', tag: 'Application', action: () => onNavigate('library') },
    { id: 'teaching', name: 'Peer Collaboration & Mentoring', emoji: '🤝', pct: 0, color: '#fb923c', tag: 'Community', action: () => onNavigate('peerconnect') },
  ];

  const apiCourses = dashboard?.courses || [];
  const courses = defaultCourses.map(def => {
    const found = apiCourses.find(c => c.id === def.id || c.name === def.name || (c.tag && c.tag.toLowerCase() === def.tag.toLowerCase()));
    return found ? { ...def, ...found, pct: found.pct || 0 } : def;
  });

  const avgCompletion = Math.round(courses.reduce((acc, c) => acc + (c.pct || 0), 0) / courses.length);

  const goals = [
    { label:'Complete 1 session',    done:(stats.attended || 0) > 0,    icon:Video },
    { label:'Browse scholarships',   done:(stats.applications || 0) > 0, icon:GraduationCap },
    { label:'Contribute a resource', done:(stats.contributed || 0) > 0,  icon:BookOpen },
  ];
  const goalsDone = goals.filter(g=>g.done).length;
  const goalsPct  = Math.round((goalsDone/goals.length)*100);

  // Dynamic calculations for Ring percentages
  const scholarshipsRing = Math.min(100, Math.round(((stats.applications || 0) * 30) + ((stats.accepted || 0) * 35)));
  const mentoringRing = Math.min(100, Math.round(((stats.attended || 0) * 25) + ((stats.taught || 0) * 35)));
  const peerConnectRing = Math.min(100, Math.round(((stats.connections || 0) * 25) + ((stats.events || 0) * 15)));
  const libraryRing = Math.min(100, Math.round(((stats.contributed || 0) * 30) + ((stats.downloads || 0) * 10) + ((stats.hoursSpent || 0) * 10)));

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>

      {/* ── HERO BANNER ── */}
      <div className="dashboard-hero-banner" style={{
        background:'linear-gradient(135deg, #f5f3ff 0%, #e0e7ff 100%)',
        borderRadius:20, padding:'24px 28px',
        alignItems:'center',
        position:'relative', overflow:'hidden',
        boxShadow:'0 10px 30px rgba(147, 51, 234, 0.05)',
        border:'1px solid rgba(147, 51, 234, 0.1)',
      }}>
        {/* Dot grid */}
        <div style={{ position:'absolute', inset:0, background: "url(\"data:image/svg+xml,%3Csvg width='50' height='50' viewBox='0 0 50 50' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='25' cy='25' r='1' fill='%239333ea' fill-opacity='0.02'/%3E%3C/svg%3E\")", borderRadius:20, pointerEvents:'none' }}/>
        <div style={{ position:'absolute', right:280, top:-50, width:250, height:250, background:'radial-gradient(circle,rgba(139,92,246,.08),transparent 70%)', borderRadius:'50%', pointerEvents:'none' }}/>

        <div style={{ position:'relative', zIndex:1 }}>
          <div style={{ display:'inline-flex', alignItems:'center', gap:6, background:'rgba(147, 51, 234, 0.06)', border:'1px solid rgba(147, 51, 234, 0.12)', borderRadius:20, padding:'4px 12px', fontSize:9, fontWeight:700, color:'var(--primary-gold)', letterSpacing:'.15em', textTransform:'uppercase', marginBottom:10, fontFamily:'monospace' }}>
            <span style={{ width:5, height:5, background:'#8b5cf6', borderRadius:'50%', animation:'ekBlink 1.2s ease-in-out infinite' }}/>Welcome Back
          </div>
          <div style={{ fontSize:28, fontWeight:800, color:'var(--navy-dark)', lineHeight:1.1, marginBottom:6 }}>
            Hello, <em style={{ fontStyle:'italic', color:'var(--primary-gold-deep)' }}>{name}</em> 👋
          </div>
          <div style={{ fontSize:12, color:'#64748b', marginBottom:18 }}>You're doing great — keep learning and climbing the ranks!</div>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems: 'center' }}>
            {[
              { icon:<span style={{fontSize:13}}>💧</span>, text:`${streak} day streak` },
              { icon:<Shield size={13} color="#64748b"/>, text:'Student · LV.4' },
            ].map(({icon,text},i) => (
              <div key={i} style={{ display:'inline-flex', alignItems:'center', gap:7, padding:'7px 14px', borderRadius:50, background:'#ffffff', border:'1px solid rgba(147,51,234,.12)', boxShadow:'0 2px 8px rgba(0,0,0,0.02)' }}>
                {icon}
                <span style={{ fontSize:12, fontWeight:700, color:'var(--navy-dark)' }}>{text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Water Pot + Water Bubble Streak */}
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6, position:'relative', zIndex:1 }}>
          <WaterPot streak={streak} todayActive={!!dashboard?.todayActive || (streak > 0)}/>
          <WaterBubbleStreak streak={streak} consistency={dashboard?.consistency} todayActive={!!dashboard?.todayActive || (streak > 0)}/>
        </div>
      </div>

      {/* ── 4 DYNAMIC STAT CARDS ── */}
      <div className="dashboard-stats-grid">
        <StatCard
          barColor="linear-gradient(90deg,#8b5cf6,#c084fc)"
          iconBg="linear-gradient(145deg,#c084fc,#8b5cf6)"
          icon={GraduationCap} title="Scholarships" ringColor="#8b5cf6" ringPct={scholarshipsRing}
          rows={[
            {dot:'#8b5cf6', label:'Total Applied', value: stats.applications || 0, valueColor:'#8b5cf6'},
            {dot:'#10b981', label:'Accepted',      value: stats.accepted || 0,     valueColor:'#10b981'},
            {dot:'#fb923c', label:'Awarded',       value: stats.awardedAmount ? `₹${stats.awardedAmount.toLocaleString('en-IN')}` : '₹0', valueColor:'#d97706'},
          ]}
          linkLabel="Explore All" onLink={() => onNavigate('scholarships')}
        />
        <StatCard
          barColor="linear-gradient(90deg,#2563eb,#60a5fa)"
          iconBg="linear-gradient(145deg,#60a5fa,#2563eb)"
          icon={Video} title="Mentoring" ringColor="#2563eb" ringPct={mentoringRing}
          rows={[
            {dot:'#2563eb', label:'Attended',    value: stats.attended || 0, valueColor:'#2563eb'},
            {dot:'#8b5cf6', label:'Taught',      value: stats.taught || 0,   valueColor:'#8b5cf6'},
            {dot:'#a78bfa', label:'Total Hours', value: `${Math.round((stats.totalMinutes || 0) / 60)} hrs`, valueColor:'#8b5cf6'},
          ]}
          linkLabel="Book Session" onLink={() => onNavigate('mentoring')}
        />
        <StatCard
          barColor="linear-gradient(90deg,#059669,#34d399)"
          iconBg="linear-gradient(145deg,#34d399,#059669)"
          icon={Users} title="Peer Connect" ringColor="#10b981" ringPct={peerConnectRing}
          rows={[
            {dot:'#10b981', label:'Connections', value: stats.connections || 0, valueColor:'#10b981'},
            {dot:'#3b82f6', label:'Group Chats', value: stats.groupChats || 0,  valueColor:'#3b82f6'},
            {dot:'#8b5cf6', label:'Events',      value: stats.events || 0,      valueColor:'#8b5cf6'},
          ]}
          linkLabel="Find Peers" onLink={() => onNavigate('peerconnect')}
        />
        <StatCard
          barColor="linear-gradient(90deg,#dc2626,#f87171)"
          iconBg="linear-gradient(145deg,#f87171,#dc2626)"
          icon={BookOpen} title="Digital Library" ringColor="#ef4444" ringPct={libraryRing}
          rows={[
            {dot:'#8b5cf6', label:'Contributed', value: stats.contributed || 0, valueColor:'#8b5cf6'},
            {dot:'#ef4444', label:'Downloaded',  value: stats.downloads || 0,   valueColor:'#ef4444'},
            {dot:'#3b82f6', label:'Hours Spent', value: `${stats.hoursSpent || 0} hrs`, valueColor:'#3b82f6'},
          ]}
          linkLabel="Browse" onLink={() => onNavigate('library')}
        />
      </div>

      {/* ── MID ROW ── */}
      <div className="dashboard-mid-grid">

        {/* Learning Progress & Curriculum Card */}
        <div style={{ background:'#ffffff', border:'1px solid rgba(0,0,0,0.05)', borderRadius:20, padding:20, display:'flex', flexDirection:'column', position:'relative', overflow:'hidden' }}>
          <div style={{ position:'absolute', top:0, left:0, right:0, height:3, background:'linear-gradient(90deg,#8b5cf6,#fb923c)', borderRadius:'20px 20px 0 0' }}/>
          <div style={{ position:'absolute', inset:0, background:'linear-gradient(160deg,rgba(0,0,0,.01) 0%,transparent 60%)', pointerEvents:'none', borderRadius:'inherit' }}/>
          
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:4, position:'relative' }}>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <div style={{ width:3, height:15, background:'linear-gradient(180deg,#8b5cf6,#7e22ce)', borderRadius:2, boxShadow:'0 2px 6px rgba(139,92,246,.2)', flexShrink:0 }}/>
              <span style={{ fontSize:14, fontWeight:700, color:'var(--navy-dark)' }}>Learning Progress & Curriculum</span>
            </div>
            <span style={{ background:'rgba(147,51,234,0.08)', color:'var(--primary-gold)', border:'1px solid rgba(147,51,234,0.2)', fontSize:10, fontWeight:800, padding:'2px 8px', borderRadius:20 }}>
              Avg: {avgCompletion}% Complete
            </span>
          </div>
          <div style={{ fontSize:10, color:'#64748b', marginBottom:14, paddingLeft:13 }}>
            5 Core Academic Tracks
          </div>

          {/* 5 Course Rows */}
          <div style={{ display:'flex', flexDirection:'column', gap:8, position:'relative' }}>
            {courses.map((c,i) => (
              <CourseRow key={i} {...c} idx={i} onClick={c.action}/>
            ))}
          </div>

          {/* Smart Daily Sprint & Habit Booster Box */}
          <div style={{ marginTop:14, padding:'12px 14px', background:'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)', borderRadius:14, border:'1px solid rgba(0,0,0,0.06)', position:'relative' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
              <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                <Target size={14} color="var(--primary-gold)"/>
                <span style={{ fontSize:11, fontWeight:800, color:'var(--navy-dark)' }}>Today's Study Sprint Goal</span>
              </div>
              <span style={{ fontSize:10, fontWeight:700, color:'#10b981' }}>
                {stats.attended > 0 ? '1/1 Session Completed' : '1 Session Remaining'}
              </span>
            </div>

            <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginTop:6 }}>
              <button
                onClick={() => onNavigate('library')}
                className="btn-lift"
                style={{ flex:1, minWidth:110, padding:'6px 10px', background:'#ffffff', border:'1px solid #e2e8f0', borderRadius:8, fontSize:10, fontWeight:700, color:'#475569', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:4, fontFamily:'inherit' }}
              >
                <BookOpen size={12} color="#ec4899"/> Study Notes
              </button>
              <button
                onClick={() => onNavigate('peerconnect')}
                className="btn-lift"
                style={{ flex:1, minWidth:110, padding:'6px 10px', background:'#ffffff', border:'1px solid #e2e8f0', borderRadius:8, fontSize:10, fontWeight:700, color:'#475569', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:4, fontFamily:'inherit' }}
              >
                <Users size={12} color="#10b981"/> Peer Study Room
              </button>
              <button
                onClick={() => onNavigate('library')}
                className="btn-lift"
                style={{ flex:1, minWidth:110, padding:'6px 10px', background:'var(--primary-gold)', border:'none', borderRadius:8, fontSize:10, fontWeight:800, color:'#ffffff', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:4, fontFamily:'inherit', boxShadow:'0 2px 6px rgba(147,51,234,0.2)' }}
              >
                <Zap size={12}/> +10m Quick Focus
              </button>
            </div>

            <div style={{ fontSize:9, color:'#64748b', marginTop:8, display:'flex', alignItems:'center', gap:4 }}>
              <Sparkles size={10} color="var(--primary-gold)"/> Spacing out daily focus builds lifelong academic mastery.
            </div>
          </div>
        </div>

        {/* Right column: Consistency Calendar + Leaderboard */}
        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
          <CalendarStreakCard streak={streak} consistency={dashboard?.consistency}/>
          <LeaderboardCard name={name} score={score} barsIn={barsIn} barRef={barRef} leaderboard={dashboard?.leaderboard || []} myRank={dashboard?.myRank}/>
        </div>
      </div>

      {/* ── BOTTOM ROW ── */}
      <div className="dashboard-bottom-grid">
        <UpcomingEventsCard events={dashboard?.events || []}/>
        <QuickActionsCard onNavigate={onNavigate} goalsDone={goalsDone} goals={goals} goalsPct={goalsPct}/>
      </div>

    </div>
  );
}

/* ──────────────────────────────────────────────
   MAIN DASHBOARD SHELL
   ────────────────────────────────────────────── */
export default function Dashboard() {
  const { user, profile, signOut, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab]                 = useState('home');
  const [showNotifications, setShowNotifications] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed]   = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem('ekalavya_sidebar_width');
    const parsed = saved ? parseInt(saved, 10) : 240;
    return isNaN(parsed) ? 240 : Math.max(180, Math.min(480, parsed));
  });
  const [isResizing, setIsResizing] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/');
    }
  }, [user, authLoading, navigate]);

  const handleLogout = async () => { await signOut(); navigate('/'); };

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Window drag listeners for smooth sidebar resizing
  useEffect(() => {
    if (!isResizing) return;
    const handleMouseMove = (e) => {
      const newWidth = Math.max(180, Math.min(480, e.clientX));
      setSidebarWidth(newWidth);
      localStorage.setItem('ekalavya_sidebar_width', String(newWidth));
      if (sidebarCollapsed) {
        setSidebarCollapsed(false);
      }
    };
    const handleMouseUp = () => {
      setIsResizing(false);
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, sidebarCollapsed]);

  if (authLoading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#ffffff', color: '#64748b' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
          <img src="/Logo.jpeg" alt="Loading" style={{ width: 44, height: 44, borderRadius: 10, animation: 'pulseSoft 1.5s infinite' }} />
          <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>Loading Dashboard...</span>
        </div>
      </div>
    );
  }

  const sbW = isMobile ? 0 : (sidebarCollapsed ? 68 : sidebarWidth);

  return (
    <div
      className={`dashboard-layout ${isResizing ? 'resizing-active' : ''}`}
      style={!isMobile ? { gridTemplateColumns: `${sbW}px 1fr` } : {}}
    >
      <style>{`
        @keyframes ekBlink{0%,100%{opacity:1}50%{opacity:0}}
        @keyframes ekFadeUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:none}}
        .ek-dash > *{animation:ekFadeUp .3s ease-out}
        .ek-nb:hover{background:rgba(0,0,0,.03)!important;border-color:rgba(0,0,0,.06)!important}
      `}</style>

      {/* ── TOPBAR ── */}
      <header style={{
        gridColumn:'1/-1', background:'#ffffff', backdropFilter:'blur(20px)',
        borderBottom:'1px solid rgba(0,0,0,.05)', display:'flex', alignItems:'center',
        padding:'0 16px', gap:12, position:'relative', zIndex:50,
      }}>
        {/* Brand */}
        <a href="/" style={{ display:'flex', alignItems:'center', gap:10, textDecoration:'none', flexShrink:0 }}>
          <img src="/Logo.jpeg" style={{ width:32, height:32, borderRadius:8, objectFit:'cover', boxShadow:'0 2px 10px rgba(0,0,0,.08)' }} alt="Ekalavya Logo" />
          {!sidebarCollapsed && <span style={{ fontSize:15, fontWeight:800, color:'var(--navy-dark)', letterSpacing:'-0.02em' }}>Ekalavya</span>}
        </a>
        
        {/* Right */}
        <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:10 }}>
          {/* Search */}
          <div style={{ position:'relative' }}>
            <Search style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:'#94a3b8' }} size={13}/>
            <input type="text" placeholder="Search courses, mentors…" style={{ width:260, background:'#f1f5f9', border:'1px solid var(--slate-200)', borderRadius:50, padding:'7px 16px 7px 36px', fontFamily:'inherit', fontSize:11, color:'var(--navy-dark)', outline:'none', transition:'border-color .2s' }}
              onFocus={e=>e.target.style.borderColor='var(--primary-gold)'} onBlur={e=>e.target.style.borderColor='var(--slate-200)'}/>
          </div>
          {/* Bell */}
          <div style={{ position:'relative' }}>
            <button className="ek-nb" onClick={() => setShowNotifications(!showNotifications)}
              style={{ width:36, height:36, borderRadius:10, background:'#f1f5f9', border:'1px solid var(--slate-200)', display:'flex', alignItems:'center', justifyContent:'center', color:'#64748b', cursor:'pointer', position:'relative', transition:'all .2s' }}>
              <Bell size={17}/>
              <span style={{ position:'absolute', top:9, right:9, width:7, height:7, background:'#8b5cf6', borderRadius:'50%', border:'2px solid #ffffff' }}/>
            </button>
            {showNotifications && (
              <div style={{ position:'absolute', right:0, top:'120%', width:320, background:'#ffffff', border:'1px solid var(--slate-200)', borderRadius:16, padding:'16px', zIndex:50, boxShadow:'0 10px 30px rgba(0,0,0,.08)' }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:12 }}>
                  <span style={{ color:'var(--navy-dark)', fontWeight:700, fontSize:13 }}>Activity Feed</span>
                  <button onClick={() => setShowNotifications(false)} style={{ color:'#64748b', cursor:'pointer', background:'none', border:'none', display:'flex' }}><X size={14}/></button>
                </div>
                <p style={{ color:'#64748b', fontSize:12, textAlign:'center', padding:'12px 0' }}>No new notifications</p>
              </div>
            )}
          </div>
          {/* Mobile-only drawer toggle */}
          {isMobile && (
            <button className="ek-nb" onClick={() => setSidebarCollapsed(c=>!c)}
              style={{ width:36, height:36, borderRadius:10, background:'#f1f5f9', border:'1px solid var(--slate-200)', display:'flex', alignItems:'center', justifyContent:'center', color:'#64748b', cursor:'pointer', transition:'all .2s' }}>
              <Menu size={17}/>
            </button>
          )}
        </div>
      </header>

      {/* ── SIDEBAR ── */}
      <aside
        className={`dashboard-sidebar ${isMobile && !sidebarCollapsed ? 'open' : ''} ${isResizing ? 'resizing' : ''}`}
        style={{
          width: isMobile ? undefined : sbW,
        }}
      >
        {/* Toggle button on the sidebar edge */}
        {!isMobile && (
          <button
            className="sidebar-collapse-toggle"
            onClick={() => setSidebarCollapsed(c => !c)}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? <ChevronRight size={15} /> : <ChevronLeft size={15} />}
          </button>
        )}

        {/* Drag Resizer Handle on desktop when expanded */}
        {!isMobile && !sidebarCollapsed && (
          <div
            className={`sidebar-resizer ${isResizing ? 'active' : ''}`}
            onMouseDown={(e) => {
              e.preventDefault();
              setIsResizing(true);
            }}
            title="Drag to resize sidebar"
          >
            <div className="sidebar-resizer-handle" />
          </div>
        )}

        <div className="sidebar-inner">
          <div style={{ padding:`${sidebarCollapsed?'14px 0 0':'14px 8px 0'}`, flex:1 }}>
            {sidebarLinks.slice(0,5).map(({ id, label, icon: Icon, color, badge }) => (
              <SidebarItem key={id} id={id} label={label} Icon={Icon} color={color} badge={badge}
                active={activeTab===id} collapsed={sidebarCollapsed}
                onClick={() => setActiveTab(id)}/>
            ))}

            <SidebarItem id="profile" label="Profile" Icon={User} color="#fb923c" badge={null}
              active={activeTab==='profile'} collapsed={sidebarCollapsed}
              onClick={() => setActiveTab('profile')}/>
          </div>
          {/* User block */}
          {!sidebarCollapsed && (
            <div style={{ padding:'8px 12px 4px', borderTop:'1px solid var(--slate-200)' }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 4px', marginBottom:6 }}>
                <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${profile?.full_name||user?.id}`} alt="avatar" style={{ width:32, height:32, borderRadius:'50%', background:'rgba(147,51,234,.05)', border:'1.5px solid rgba(147,51,234,.12)' }}/>
                <div style={{ minWidth:0 }}>
                  <div style={{ fontSize:11, fontWeight:700, color:'var(--navy-dark)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{profile?.full_name||'Scholar'}</div>
                  <div style={{ fontSize:9, color:'#64748b', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{user?.email}</div>
                </div>
              </div>
            </div>
          )}
          <div style={{ padding:'4px 8px 8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {profile?.role === 'admin' && (
              <button onClick={() => navigate('/admin')}
                style={{ display:'flex', alignItems:'center', justifyContent: sidebarCollapsed?'center':'flex-start', gap:8, padding:'10px', borderRadius:12, background:'rgba(139,92,246,.04)', border:'1px solid rgba(139,92,246,.12)', fontSize:11, fontWeight:700, color:'#8b5cf6', cursor:'pointer', width:'100%', letterSpacing:'.08em', textTransform:'uppercase', transition:'all .2s', fontFamily:'inherit' }}
                onMouseEnter={e => { e.currentTarget.style.background='rgba(139,92,246,.08)'; e.currentTarget.style.transform='translateY(-1px)'; }}
                onMouseLeave={e => { e.currentTarget.style.background='rgba(139,92,246,.04)'; e.currentTarget.style.transform='translateY(0)'; }}>
                <Shield size={16}/>
                {!sidebarCollapsed && <span>Admin Panel</span>}
              </button>
            )}
            <button onClick={handleLogout}
              style={{ display:'flex', alignItems:'center', justifyContent: sidebarCollapsed?'center':'flex-start', gap:8, padding:'10px', borderRadius:12, background:'rgba(239,68,68,.04)', border:'1px solid rgba(239,68,68,.12)', fontSize:11, fontWeight:700, color:'#ef4444', cursor:'pointer', width:'100%', letterSpacing:'.08em', textTransform:'uppercase', transition:'all .2s', fontFamily:'inherit' }}
              onMouseEnter={e => { e.currentTarget.style.background='rgba(239,68,68,.08)'; e.currentTarget.style.transform='translateY(-1px)'; }}
              onMouseLeave={e => { e.currentTarget.style.background='rgba(239,68,68,.04)'; e.currentTarget.style.transform='translateY(0)'; }}>
              <LogOut size={13}/>{!sidebarCollapsed && 'Log Out'}
            </button>
          </div>
        </div>
      </aside>

      {/* ── MAIN CONTENT ── */}
      <main style={{ overflowY:'auto', overflowX:'hidden', padding:18, display:'flex', flexDirection:'column', gap:16, height:'100%' }} className="ek-dash">
        {activeTab === 'home'         && <DashHomeTab user={user} profile={profile} onNavigate={setActiveTab}/>}
        {activeTab === 'scholarships' && <Scholarships/>}
        {activeTab === 'mentoring'    && <Mentors/>}
        {activeTab === 'peerconnect'  && <Community/>}
        {activeTab === 'library'      && <Library/>}
        {activeTab === 'profile'      && <Profile/>}
        {activeTab === 'settings'     && (
          <div style={{ textAlign:'center', padding:'5rem', color:'#64748b' }}>
            <Settings size={48} style={{ margin:'0 auto 1rem' }}/>
            <h3 style={{ color:'var(--navy-dark)', marginBottom:'.5rem' }}>Settings</h3>
            <p>Account settings coming soon.</p>
          </div>
        )}
      </main>
    </div>
  );
}

/* ─── Sidebar Item ─── */
function SidebarItem({ id, label, Icon, color, badge, active, collapsed, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      data-label={label}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      onClick={onClick}
      style={{
        display:'flex', alignItems:'center', gap: collapsed?0:10,
        padding: collapsed ? '9px 0' : '8px 10px 8px 12px',
        justifyContent: collapsed ? 'center' : 'flex-start',
        borderRadius:12, fontSize:12, fontWeight:600, cursor:'pointer',
        color: active ? color : hov ? 'var(--navy-dark)' : '#64748b',
        background: active ? `${color}0e` : hov ? 'rgba(0,0,0,0.03)' : 'transparent',
        border:`1px solid ${active ? color+'20' : hov ? 'rgba(0,0,0,0.04)' : 'transparent'}`,
        boxShadow: active ? `0 4px 15px rgba(139,92,246,.08)` : 'none',
        transform: hov && !active ? 'translateX(3px)' : 'translateX(0)',
        transition:'all .2s ease', width:'100%', textAlign:'left', position:'relative',
        fontFamily:'inherit', marginBottom:3, whiteSpace:'nowrap', overflow:'visible',
      }}
    >
      {active && <span style={{ position:'absolute', left:0, top:'20%', bottom:'20%', width:3, background:`linear-gradient(180deg,${color},${color}99)`, borderRadius:'0 3px 3px 0', boxShadow:`0 0 8px ${color}50` }}/>}
      <span style={{
        width:32, height:32, borderRadius:9, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0,
        background: active ? `linear-gradient(145deg,${color},${color}bb)` : hov ? `${color}12` : '#f1f5f9',
        border:`1px solid ${active ? color+'25' : 'rgba(0,0,0,0.06)'}`,
        boxShadow:`0 2px 5px rgba(0,0,0,.02),inset 0 1px 0 rgba(255,255,255,.25)`,
        transform: hov ? 'scale(1.1) translateY(-1px)' : 'scale(1)',
        transition:'all .2s', color: active ? '#fff' : hov ? color : '#64748b',
        position:'relative', overflow:'hidden',
      }}>
        <span style={{ position:'absolute', inset:0, background:'linear-gradient(150deg,rgba(255,255,255,.15) 0%,transparent 55%)', borderRadius:'inherit' }}/>
        <Icon size={16}/>
      </span>
      {!collapsed && <span style={{ flex:1 }}>{label}</span>}
      {!collapsed && badge && (
        <span style={{ background:'linear-gradient(135deg,#c084fc,#7e22ce)', color:'#ffffff', fontSize:9, fontWeight:800, padding:'2px 6px', borderRadius:20, fontFamily:'monospace', boxShadow:'0 2px 8px rgba(192,132,252,.2)' }}>{badge}</span>
      )}
      {/* Tooltip when collapsed */}
      {collapsed && (
        <span style={{ position:'absolute', left:'calc(100% + 10px)', top:'50%', transform:'translateY(-50%)', background:'#ffffff', border:'1px solid var(--slate-200)', color:'var(--navy-dark)', fontSize:11, fontWeight:600, padding:'5px 10px', borderRadius:8, whiteSpace:'nowrap', pointerEvents:'none', opacity: hov?1:0, transition:'opacity .15s', boxShadow:'0 4px 16px rgba(0,0,0,.05)', zIndex:200 }}>
          {label}
        </span>
      )}
    </button>
  );
}
